import React, { useState } from 'react';
import DatePicker from 'ui-components/v2/DatePicker';
import { AVAILABLE_DAY_DATE_FORMAT } from 'constants/time';

import { pushMetricsEvent } from 'hooks/useDataLayer';
import AdjustedShippingDay from './AdjustedShippingDay';
import { DeliveryWindowPickerProps } from './interface';
import { useDeliveryDates } from './useDeliveryDates';

const DeliveryWindowPicker = ({
  productId,
  fieldName,
  control,
  purchasable,
  setShippingOptions,
  ...rest
}: DeliveryWindowPickerProps) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const {
    name,
    error,
    label,
    disabled,
    getMaxDate,
    initialDate,
    getHelperText,
    availableDateMap,
    handleDateChange,
    handleDateAccept,
    handleDateBlur,
    shouldDisableDate,
    handleMonthChange,
    initiallyHighlightedDate,
  } = useDeliveryDates({
    control,
    fieldName,
    productId,
    purchasable,
    setShippingOptions,
  });

  const CalendarLegend = () => (
    <div className="px-[18px] pb-5 sm:px-7">
      <p className="m-0">
        <span className="bullet bg-gray-500" />
        Shipping Price
      </p>
    </div>
  );

  return (
    <DatePicker
      disablePast
      name={name}
      label={label}
      id={`pdp-delivery-${name}-input`}
      className="tracking-delivery-date-input-container"
      error={error}
      disabled={disabled}
      value={initialDate}
      disableToolbar={false}
      onChange={handleDateChange}
      onAccept={handleDateAccept}
      InputProps={{ onBlur: handleDateBlur }}
      helperText={getHelperText()}
      onMonthChange={handleMonthChange}
      ToolbarComponent={CalendarLegend}
      shouldDisableDate={shouldDisableDate}
      initialFocusedDate={initiallyHighlightedDate}
      maxDate={getMaxDate({ amount: 6, unit: 'month' })}
      open={pickerOpen}
      onOpen={() => {
        setPickerOpen(true);
        pushMetricsEvent('ga4', { event: 'calendar_open' });
      }}
      onClose={() => setPickerOpen(false)}
      renderDay={(day, selectedDate, dayInCurrentMonth, dayComponent) => {
        if (!day) return dayComponent;

        const availableDate =
          availableDateMap[day.format(AVAILABLE_DAY_DATE_FORMAT)];

        if (availableDate) {
          // MUI's DayWrapper only attaches click/keypress handlers when the
          // day is in the current month
          const handleOutOfMonthClick = !dayInCurrentMonth
            ? () => {
                handleDateChange(day);
                handleDateAccept(day);
                setPickerOpen(false);
              }
            : undefined;

          return (
            <AdjustedShippingDay
              day={day.date()}
              selected={!!selectedDate?.isSame(day)}
              price={`${availableDate.shipping_price}`}
              priceDiff={`${availableDate.shipping_price_diff}`}
              overriden={availableDate.overriden}
              onClick={handleOutOfMonthClick}
              ariaLabel={`${day.format(
                'dddd, MMMM D, YYYY',
              )}, shipping $${availableDate.shipping_price}`}
            />
          );
        }

        // Unavailable/disabled days: full-date accessible name instead of an
        // ambiguous bare day number (WCAG 4.1.2). Out-of-month filler days are
        // only visually hidden by MUI (opacity 0) — hide them from assistive
        // tech. (Out-of-month *available* days render above and stay exposed.)
        return dayInCurrentMonth
          ? React.cloneElement(dayComponent, {
              'aria-label': day.format('dddd, MMMM D, YYYY'),
            })
          : React.cloneElement(dayComponent, { 'aria-hidden': true });
      }}
      {...rest}
    />
  );
};

export default DeliveryWindowPicker;
